import { capitalize, flatMap, flatten } from "lodash";
import { flow, map, replace, split } from "lodash/fp";

const sumLength = (items: string[]): number => items.reduce((cur, x) => (cur > 0 ? cur + 1 : cur) + x.length, 0);

/**
 *	Returns the shortened name using initials.
 *
 *	@param name name to shorten
 *	@param opts =
 *		maxLength: maximum length to try to keep the name under.
 *		direction: whether to shorten forwards (1) or backwards (-1)
 *		separator: leave this at the default of " "
 */
export const getInitials = (
	name: string,
	opts: { maxLength?: number; direction?: number; separator?: string; curLength?: number; keepFirst?: boolean } = {},
): string => {
	let asc, step;
	let i, n;
	const { maxLength = null, direction = 1, separator = " ", curLength = 0, keepFirst = false } = opts;

	const names = name.split(separator);
	const getInitial = (namePart: string): string => {
		if (separator === "-") {
			return namePart[0] ? namePart[0].toUpperCase() : "";
		}
		return getInitials(namePart, { ...opts, separator: "-", curLength: sumLength(names) });
	};

	const iterable = names.slice(1, -1);
	for (
		step = direction, asc = step > 0, i = asc ? 0 : iterable.length - 1;
		asc ? i < iterable.length : i >= 0;
		i += step
	) {
		n = iterable[i];
		if (maxLength && maxLength < (curLength || sumLength(names))) {
			names[i + 1] = getInitial(n);
		}
	}

	if (maxLength && maxLength < (curLength || sumLength(names))) {
		if (direction === 1) {
			names[names.length - 1] = getInitial(names[names.length - 1]);
		} else {
			names[0] = getInitial(names[0]);
		}
	}

	if (!keepFirst && maxLength && maxLength < (curLength || sumLength(names))) {
		if (direction === 1) {
			names[0] = getInitial(names[0]);
		} else {
			names[names.length - 1] = getInitial(names[names.length - 1]);
		}
	}

	let output = "";
	for (i = 0; i < names.length; i++) {
		n = names[i];
		if (i === names.length - 1 || (n.length <= 1 && names[i + 1].length <= 1)) {
			output += n;
		} else {
			output += n + separator;
		}
	}

	return output;
};

export const listFormat = <T>(
	list: readonly T[],
	opts: { sep: string | ((x: T, y: number) => (string | T) | (string | T)[]); last: string } = {
		sep: ", ",
		last: " and ",
	},
): (string | T)[] => {
	const { sep, last } = opts;
	const len = list.length;

	let sepFunc: (x: T, y: number) => (string | T) | (string | T)[];

	if (typeof sep === "string") {
		sepFunc = ((separator: string, lastSeparator: string) => (x: T | string, i: number) => {
			if (i >= len - 1) {
				return x;
			}
			if (lastSeparator) {
				return [x, i === len - 2 ? (len > 2 ? separator.trim() : "") + lastSeparator : separator];
			}
			return [x, separator];
		})(sep, last);
	} else {
		sepFunc = sep;
	}

	return flatten(list.map((x: T, i: number) => sepFunc(x, i)));
};

export const insertBetween = <T>(array: T[], element: T): boolean[] =>
	flatMap(array, (value: T, index: number, original: typeof array) => {
		if (original.length - 1 !== index) {
			return [value, element];
		}
		return value;
	});

export const splitInsert = (text: string, splitBy: string, insert: string): boolean[] =>
	insertBetween(text.split(splitBy), insert);

export const nameFromNameEntry = (entry: string): string => {
	return flow(
		split(" "),
		map(x => capitalize(x)),
	)(entry).join(" ");
};

export const emailFromNameEntry = (entry: string, domain: string): string => {
	const email = entry.toLowerCase().split(" ");
	return `${email.join(".")}@${domain}`;
};

export const nameFromEmailEntry = (entry: string): string => {
	const head = entry.split("@")[0];
	return flow(
		replace(/\W+/g, " "),
		split(" "),
		map(x => capitalize(x)),
	)(head).join(" ");
};

/**
 * Truncate a string to fit within a certain length, adding ellipsis at the end
 * if the text was truncated.
 *
 * @param text The text to truncate
 * @param length The maximum length of the output
 */
export function truncate(text: string, length: number): string {
	if (text.length <= length) {
		return text;
	}
	return `${text.substring(0, length - 1)}\u{2026}`;
}

/**
 * Pluralize a word depending on the value of `n`
 *
 * @param n The number in question
 * @param singular The singular form of the word
 * @param pluralForm The plural form of the word – defaults to `singular` with an S on the end
 */
export function nGetText(n: number, singular: string, pluralForm = `${singular}s`): string {
	return n === 1 ? singular : pluralForm;
}

export function plural(number: number) {
	if (number !== 1) {
		return "s";
	}
	return "";
}

const ROMAN_DIGITS = [
	"",
	"C",
	"CC",
	"CCC",
	"CD",
	"D",
	"DC",
	"DCC",
	"DCCC",
	"CM",
	"",
	"X",
	"XX",
	"XXX",
	"XL",
	"L",
	"LX",
	"LXX",
	"LXXX",
	"XC",
	"",
	"I",
	"II",
	"III",
	"IV",
	"V",
	"VI",
	"VII",
	"VIII",
	"IX",
].map(i => i.toLowerCase());

// https://stackoverflow.com/a/9083076/4427600
export function toRomanOrder(num: number) {
	const digits = String(+num).split("");
	let roman = "";
	let i = 3;
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	while (i--) roman = (ROMAN_DIGITS[+digits.pop()! + i * 10] || "") + roman;
	return Array(+digits.join("") + 1).join("m") + roman;
}

export function toAlphabeticalOrder(num: number) {
	const START = "a".charCodeAt(0);

	// https://stackoverflow.com/a/62198235/4427600
	function numberToBase26(val: number, tail = ""): string {
		if (val <= 26) {
			return `${String.fromCharCode(val + START - 1)}${tail}`;
		}

		const remainder = val % 26 || 26;
		const division = Math.trunc(val / 26) - (remainder === 26 ? 1 : 0);
		return numberToBase26(division, `${String.fromCharCode(remainder + START - 1)}${tail}`);
	}

	return numberToBase26(num);
}

export function capitalizeFirstLetter(value: string) {
	return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

export function decapitalizeFirstLetter(value: string) {
	return `${value.charAt(0).toLowerCase()}${value.slice(1)}`;
}

export const simpleHash = (s: string) => s.split("").reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);

export const displayNoteType = (noteType: string, shared?: boolean) => {
	if (shared) {
		return "Shared";
	}

	switch (noteType) {
		case "meeting":
			return "Meetings";
		case "goalset":
			return window.APP_CONFIG ? window.APP_CONFIG.prioritiesLabel : "Priorities";
		case "oneonone":
			return "1-on-1s";
		case "personal":
			return "Private";
		default:
			return noteType || "";
	}
};
